import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, LayoutDashboard, Target, Shield, DollarSign, BookOpen, HelpCircle, Contact, CheckSquare, CalendarDays, Settings, FolderOpen, PlayCircle, Users, Bell, Search, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';

const TOUR_STEPS = [
  {
    target: '[data-testid="nav-overview"]',
    title: 'Welcome to Enable Healthcare',
    description: 'This is your command center. View KPIs, pipeline stats, and commission estimates all in one place. Track your performance at a glance.',
    icon: LayoutDashboard,
    position: 'right'
  },
  {
    target: '[data-testid="nav-leads"]',
    title: 'Lead Management',
    description: 'Submit, track, and manage your referral leads. Switch between List, Kanban Board, and By Agent views. Admins see all leads grouped by agent by default.',
    icon: Target,
    position: 'right'
  },
  {
    target: '[data-testid="nav-leads"]',
    title: 'Pipeline Stages',
    description: 'Leads flow through: New Lead, Contacted, Demo Scheduled, Demo Completed, Qualified, Payment Requested, and Paid. You manage the first three stages; Admin handles qualification and payouts.',
    icon: Shield,
    position: 'right'
  },
  {
    target: '[data-testid="nav-contacts"]',
    title: 'Contacts',
    description: 'Manage your contact database. Import contacts via CSV or add them manually. Contacts can be linked to leads for easy tracking.',
    icon: Contact,
    position: 'right'
  },
  {
    target: '[data-testid="nav-activities"]',
    title: 'Tasks & Activities',
    description: 'Stay organized with your task list. Create follow-up tasks, set due dates, and track progress on all your lead-related activities.',
    icon: CheckSquare,
    position: 'right'
  },
  {
    target: '[data-testid="nav-commissions"]',
    title: 'Commission Tracking',
    description: 'Track your earnings in real time. Use the built-in commission calculator to estimate payouts for each product type. See pending and paid commissions.',
    icon: DollarSign,
    position: 'right'
  },
  {
    target: '[data-testid="nav-calendar"]',
    title: 'Calendar',
    description: 'Schedule and manage meetings, demos, and follow-ups. Sync with Microsoft 365 or Google Calendar to keep everything in one place.',
    icon: CalendarDays,
    position: 'right'
  },
  {
    target: '[data-testid="nav-guidelines"]',
    title: 'Guidelines & Training',
    description: 'Access step-by-step sales guides, compliance documents, and best practices. Everything you need to succeed is organized here.',
    icon: BookOpen,
    position: 'right'
  },
  {
    target: '[data-testid="nav-media"]',
    title: 'Marketing Collateral',
    description: 'Download brochures, presentations, and marketing materials to share with prospects. All assets are kept up to date by your admin team.',
    icon: FolderOpen,
    position: 'right'
  },
  {
    target: '[data-testid="nav-videos"]',
    title: 'Video Library',
    description: 'Watch training videos, product demos, and sales technique tutorials. A library of resources to sharpen your skills.',
    icon: PlayCircle,
    position: 'right'
  },
  {
    target: '[data-testid="universal-search"]',
    title: 'AI-Powered Search',
    description: 'Search across everything — leads, guidelines, media, and videos. AI generates instant summaries of your results so you can find what you need fast.',
    icon: Search,
    position: 'bottom'
  },
  {
    target: '[data-testid="notification-bell"]',
    title: 'Notifications',
    description: 'Real-time alerts when leads are qualified, payments approved, or any status changes. Never miss an important update.',
    icon: Bell,
    position: 'bottom-left'
  },
  {
    target: '[data-testid="nav-settings"]',
    title: 'Profile & Settings',
    description: 'Update your profile, add your About Me (expertise, business plan, focus areas), connect Microsoft or Google accounts, and manage notification preferences.',
    icon: Settings,
    position: 'right'
  },
  {
    target: '[data-testid="header-tour-btn"]',
    title: "You're All Set!",
    description: 'You can restart this tour anytime by clicking this help icon. Now go close some deals!',
    icon: HelpCircle,
    position: 'bottom-left'
  }
];

const ProductTour = ({ show, onClose }) => {
  const [step, setStep] = useState(0);
  const [highlightBox, setHighlightBox] = useState(null);

  const updateHighlight = useCallback(() => {
    const currentStep = TOUR_STEPS[step];
    if (!currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setHighlightBox({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setHighlightBox(null);
    }
  }, [step]);

  // Reset step only when tour opens
  useEffect(() => {
    if (show) {
      setStep(0);
    }
  }, [show]);

  // Update highlight when step changes or on resize
  useEffect(() => {
    if (show) {
      updateHighlight();
      window.addEventListener('resize', updateHighlight);
      return () => window.removeEventListener('resize', updateHighlight);
    }
  }, [show, step, updateHighlight]);

  if (!show) return null;

  const currentStep = TOUR_STEPS[step];
  const Icon = currentStep.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  // Calculate tooltip position
  let tooltipStyle = {};
  if (highlightBox) {
    if (currentStep.position === 'right') {
      tooltipStyle = { top: Math.max(20, highlightBox.top - 10), left: highlightBox.left + highlightBox.width + 16 };
    } else if (currentStep.position === 'bottom-left') {
      tooltipStyle = { top: highlightBox.top + highlightBox.height + 16, left: Math.max(20, highlightBox.left - 280) };
    } else if (currentStep.position === 'bottom') {
      tooltipStyle = { top: highlightBox.top + highlightBox.height + 16, left: Math.max(20, highlightBox.left) };
    } else {
      tooltipStyle = { top: highlightBox.top + highlightBox.height + 16, left: highlightBox.left };
    }
  } else {
    tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const handleFinish = () => {
    localStorage.setItem('enable_tour_completed', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100]" data-testid="product-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleFinish} />
      
      {/* Highlight ring */}
      {highlightBox && (
        <div
          className="absolute z-[101] rounded-xl ring-4 ring-[#0071E3] ring-offset-4 ring-offset-transparent pointer-events-none transition-all duration-300"
          style={{
            top: highlightBox.top - 4,
            left: highlightBox.left - 4,
            width: highlightBox.width + 8,
            height: highlightBox.height + 8,
          }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-[102] w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={tooltipStyle}
          data-testid="tour-tooltip"
        >
          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div className="h-1 bg-[#0071E3] transition-all duration-300" style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }} />
          </div>
          
          <div className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-[#1B365D]/10 rounded-lg flex-shrink-0">
                <Icon className="w-5 h-5 text-[#1B365D]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1B365D]">{currentStep.title}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{currentStep.description}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">{step + 1} of {TOUR_STEPS.length}</span>
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} data-testid="tour-prev">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleFinish} className="text-slate-500" data-testid="tour-skip">
                  Skip
                </Button>
                {isLast ? (
                  <Button size="sm" className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" onClick={handleFinish} data-testid="tour-finish">
                    Get Started
                  </Button>
                ) : (
                  <Button size="sm" className="bg-[#0071E3] hover:bg-[#0071E3]/90 rounded-full" onClick={() => setStep(s => s + 1)} data-testid="tour-next">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ProductTour;
